import { HealthcareService } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/HealthcareService';
import { Location } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/Location';
import { Organization } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/Organization';
import { WrappedValueSet } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { PaginateQuery, paginateQueryToURLParams } from 'src/api/pagination/dtos';
import { fhirClient } from 'src/App';
import { CreateReferralDto } from 'src/crs/referral/dto/create-referral.dto';
import { ReferralDto } from 'src/crs/referral/dto/referral.dto';
import { UpdateReferralDto } from 'src/crs/referral/dto/update-referral.dto';
import { ActivityDefinition, Bundle, PlanDefinition } from 'src/careflow/ph-core/fhir-client/resources';
import { getValueSetConceptValue } from 'src/careflow/helpers/common';
import { getRegistries } from 'src/careflow/ph-core/multi-tenant/registry/registry';
import { getDynamicValueExpressionByPath, getFhirIdFromEntity } from 'src/utils/fhir';
import { convertFilterToPaginateQueryParams } from 'src/utils/gridView';
import { apiClient } from 'src/careflow/ph-core/api_client/api_client';
import { ContactAttemptDto } from 'src/crs/dto/contact-attempt.dto';

export interface ReferralResponse {
  createdBy: string;
  createdOn: string;
  updatedBy: string;
  updatedOn: string;
  referralId: string;
  referralUUID: string;
  fhirId: string;
  workflowInstanceId: number;
  workflowStageId: string;
  workflowStageName: string;
  workflowStageOrder: number;
  workflowStatus: string;
  workflowScope: string;
  workflowSubstatus: string;
  workflowDispositionReason: string;
  workflowDispositionNote: string;
  workflowDispositionOn: string;
  workflowDispositionAt: string;
  workflowOwnedBy: string;
  indexedFhirRefUri: string;
  indexedMintReferralId: string;
  indexedsubjectMrn: string;
  indexedsubjectName: string;
  indexedsubjectPhone: string;
  indexedsubjectEmail: string;
  indexedsubjectDob: string;
  indexedSubjectGender: string;
  indexedSubjectRace: string;
  indexedSubjectEthnicity: string;
  indexedRegistrationDate: string;
  indexedStatus: string;
  indexedRequesterFhirRefUri: string;
  indexedRequesterName: string;
  indexedPerformerName: string;
  internalNumber: string;
  createdOnFormatted: string;
  subjectDOBFormatted: string;
  subjectAge: string;
}

export interface PaginatedReferralResponse {
  data: ReferralResponse[];
  links: {
    current: string;
  };
  meta: {
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
  };
}

export interface PaginatedReferralDto {
  data: ReferralDto[];
  links: {
    current: string;
  };
  meta: {
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
  };
}

export interface ReferralContactAttemptResponse {
  referralId: string;
  fhirId: string;
  referralContactAttempId: string;
  referralContactAttempUuid: string;
  referralContactAttempOutcome: string;
  referralContactAttempContactOn: string;
  referralContactAttempNextContactOn: string;
  referralContactAttempNoteId: string;
  noteId: string;
  noteUuid: string;
  noteType: string;
  noteText: string;
}
export interface AssessmentsFormsResponse {
  generic_id: number;
  survey_id: any;
  response_id: any;
  start_language: any;
  survey_type: any;
  submit_date: any;
}

export interface ServiceByFiltersResponse {
  healthcareService?: HealthcareService;
  organization?: Organization;
  location?: Location;
}

const apiBase = 'crs/referral';

const ReferralApi = {

  getAll: async (queryParams: PaginateQuery): Promise<PaginatedReferralResponse | undefined> => {
    const urlParams = paginateQueryToURLParams(queryParams);
    const filters = convertFilterToPaginateQueryParams(queryParams?.filter ?? {});
    const url = `${apiBase}/?${urlParams.toString()}${filters}`;

    return apiClient.get<PaginatedReferralResponse | undefined>(url);
  },

  getOne: async (fhirId: string): Promise<ReferralResponse | undefined> => {
    return apiClient.get<ReferralResponse | undefined>(
      `${apiBase}/${fhirId}`
    );
  },

  assessments: async (surveyId: any, reference: any): Promise<any> => {
    return apiClient.get<any | undefined>(
      `crs/assessments/${surveyId}?reference=${reference}`
    );
  },

  surveyDetail: async (responseId: any): Promise<any> => {
    return apiClient.get<any | undefined>(
      `crs/assessments-template/${responseId}`
    );
  },

  assessmentsByPatient: async (fhirPatientId: any): Promise<any> => {
    return apiClient.get<any | undefined>(
      `crs/assessments-patient/${fhirPatientId}`
    );
  },

  create: async (payload: CreateReferralDto): Promise<ReferralResponse | undefined> => {
    return apiClient.post<CreateReferralDto,ReferralResponse | undefined>(
      `${apiBase}/`, payload
    );
  },

  update: async (fhirId: string, payload: UpdateReferralDto): Promise<ReferralResponse | undefined> => {
    return apiClient.put<UpdateReferralDto,ReferralResponse | undefined>(
      `${apiBase}/${fhirId}`, payload
    );
  },

  patch: async (fhirId: string, payload: UpdateReferralDto): Promise<ReferralResponse | undefined> => {
    return apiClient.patch<UpdateReferralDto,ReferralResponse | undefined>(
      `${apiBase}/${fhirId}`, payload
    );
  },

  getAllContactAttempt: async (
    fhirId: string
  ): Promise<ReferralContactAttemptResponse[] | undefined> => {
    return apiClient.get<ReferralContactAttemptResponse[] | undefined>(
      `${apiBase}/contact-attempt/${fhirId}`
    );
  },

  createContactAttempt: async (payload: ContactAttemptDto): Promise<any | undefined> => {
    return apiClient.post<ContactAttemptDto, any | undefined>(
      `${apiBase}/contact-attempt`,
      payload
    );
  },

  getServiceRequestsBasedOnResource: async (resource: string): Promise<ReferralResponse[]> => {
    return apiClient.get<ReferralResponse[]>(
      `${apiBase}/basedon/${resource}`
    );
  },

  createChildReferral: async (payload: any) => {
    return apiClient.post<any, undefined>(
      `${apiBase}/child-referral`,
      payload
    );
  },

  updateChildReferral: async (fhirId: string, payload: any) => {
    return apiClient.put<any, undefined>(
      `${apiBase}/child-referral/${fhirId}`,
      payload
    );
  },

  getServicesByFilters: async (filters: any) => {
    const nonEmptyFilters = Object.entries(filters).reduce<Record<string, string>>(
      (filters, [key, value]) => {
        if (!value) return filters;
        filters[key] = value as string;
        return filters;
      },
      {}
    );

    const queryParams = new URLSearchParams(nonEmptyFilters).toString();

    let backUrl = `${apiBase}/child-referral/service${queryParams ? `?${queryParams}` : ''}`;

    return apiClient.get<ServiceByFiltersResponse[] | undefined>(
      backUrl
    );
  },
  
  sync: async (id: string) => {
    return apiClient.post(`${apiBase}/${id}/sync`,null);
  }
}

export const ReferralService = {

  getAll: async (queryParams: PaginateQuery): Promise<PaginatedReferralDto | undefined> => {
    const referrals = await ReferralApi.getAll(queryParams);
    if (!referrals) return undefined;
    const dtos = referrals.data.map((r) => {
      const dto = new ReferralDto();
      dto.fromReferralResponse(r);
      return dto;
    });
    const mapped: PaginatedReferralDto = { ...referrals, data: dtos };
    return mapped;
  },

  getOne: async (fhirId: string): Promise<ReferralResponse | undefined> => {
    const referral = await ReferralApi.getOne(fhirId);
    if (!referral) return undefined;
    return referral;
  },

  assessments: async (surveyId: any, reference: any): Promise<any> => {
    const assessments = ReferralApi.assessments(surveyId,reference);
    if (!assessments) return undefined;
    return assessments;
  },

  assessmentsByPatient: async (fhirPatientId: any): Promise<any> => {
    const assessmentByPatient = await ReferralApi.assessmentsByPatient(fhirPatientId);
    if (!assessmentByPatient) return undefined;
    return assessmentByPatient;
  },

  surveyDetail: async (responseId: any) => {
    const surveyDetail = await ReferralApi.surveyDetail(responseId);
    if (!surveyDetail) return undefined;
    return surveyDetail;
  },

  create: async (payload: CreateReferralDto): Promise<ReferralDto | undefined> => {
    const referral = await ReferralApi.create(payload);
    if (!referral) return undefined;
    const dto = new ReferralDto();
    dto.fromReferralResponse(referral);
    return dto;
  },

  update: async (fhirId: string, payload: UpdateReferralDto): Promise<ReferralDto | undefined> => {
    const referral = await ReferralApi.update(fhirId, payload);
    if (!referral) return undefined;
    const dto = new ReferralDto();
    dto.fromReferralResponse(referral);
    return dto;
  },

  getContactAttempt: async (fhirId: string): Promise<ReferralContactAttemptResponse[] | undefined> => {
    const contactAttempts = await ReferralApi.getAllContactAttempt(fhirId);
    if (!contactAttempts) return undefined;
    return contactAttempts;
  },

  createContactAttempt: async (payload: ContactAttemptDto): Promise<any | undefined> => {
    const contactAttempt = await ReferralApi.createContactAttempt(payload);
    if (!contactAttempt) return undefined;
    return contactAttempt;
  },

  getServiceRequestsBasedOnResource: async (
    resourceType: string,
    fhirId: string
  ): Promise<ReferralResponse[]> => {
    const serviceRequests = await ReferralApi.getServiceRequestsBasedOnResource(
      `${resourceType}/${fhirId}`
    );
    return serviceRequests;
  },

  createChildReferral: async (payload: any): Promise<any | undefined> => {
    const response = await ReferralApi.createChildReferral(payload);
    if (!response) return undefined;
    return response;
  },

  updateChildReferral: async (fhirId: string, payload: any): Promise<any | undefined> => {
    const response = await ReferralApi.updateChildReferral(fhirId, payload);
    if (!response) return undefined;
    return response;
  },

  getServicesByFilters: async (data: any): Promise<ServiceByFiltersResponse[] | undefined> => {
    const response = await ReferralApi.getServicesByFilters(data);
    if (!response) return undefined;
    return response;
  },

  getPlanActivityDefinition: async (
    requestIntentVS: WrappedValueSet | null, 
    referralServicesInternal: WrappedValueSet | null,
    referralStatus: WrappedValueSet | null
  ) => {
    try {
      const registry = await getRegistries(undefined, 'nat-plan-definition');
      const natPlanDefinitionNames: string[] = registry?.[0]?.keyValue ? JSON.parse(registry?.[0]?.keyValue) : [];
      const planDefinitionResourcesOther = natPlanDefinitionNames?.length 
        ? await fhirClient.search(`PlanDefinition?name=${natPlanDefinitionNames?.join(",")}`)
        : null;
      const planDefinitionResources = await fhirClient.search(`PlanDefinition?topic=referral`);

      if (!planDefinitionResources?.entry?.length && !planDefinitionResourcesOther?.entry?.length) return [];
      const entries = [...planDefinitionResources?.entry ?? [], ...planDefinitionResourcesOther?.entry ?? []]
      const entrieResourcePD = entries.reduce<(PlanDefinition)[]>((resources, { resource }) => {
        if (!resource) return resources;
        return [...resources, resource as PlanDefinition];
      }, []);

      const activityDefinitionIds: string[] = entrieResourcePD?.reduce((acc, e) => {
        const definitions = e?.action
          ?.map((s) => getFhirIdFromEntity(s?.definitionCanonical || ''))
          .filter((item): item is string => item !== undefined) ?? [];
        return acc.concat(definitions);
      }, [] as string[]) ?? [];

      const activityDefinitionResources = await fhirClient.search(`ActivityDefinition?_id=${activityDefinitionIds?.join(',')}`);
      if (!activityDefinitionResources?.entry?.length) return [];
      const entrieResourceAD = activityDefinitionResources.entry.reduce<(ActivityDefinition)[]>((resources, { resource }) => {
        if (!resource) return resources;
        return [...resources, resource as ActivityDefinition];
      }, []);

      const validPlanDefinitions = entrieResourcePD.filter(planDefinition => {
        if (!planDefinition.action || planDefinition.action.length === 0) return true;
      
        const isNAT = natPlanDefinitionNames?.find((pd) => pd === planDefinition?.name);
        if (isNAT) return true;

        return planDefinition.action.every(action => {
          const actId = getFhirIdFromEntity(action.definitionCanonical || '');
          return !!entrieResourceAD.find(ad => ad.id === actId);
        });
      });

      const mapPlanActivityDefinition = validPlanDefinitions.map((planDefinition) => {
        const relatedActivityDefinition = entrieResourceAD?.find((e) => 
          e?.id === getFhirIdFromEntity(planDefinition?.action?.[0]?.definitionCanonical || '')
        );

        const serviceRequestIntent = getValueSetConceptValue(
          requestIntentVS?.asListAll?.() ?? [],
          getDynamicValueExpressionByPath(relatedActivityDefinition?.dynamicValue, 'serviceRequest.intent')
        );

        const serviceRequestStatus = getValueSetConceptValue(
          referralStatus?.asListAll?.() ?? [],
          getDynamicValueExpressionByPath(relatedActivityDefinition?.dynamicValue, 'serviceRequest.status')
        );

        const serviceRequestCode = getValueSetConceptValue(
          referralServicesInternal?.asListAll() ?? [],
          getDynamicValueExpressionByPath(relatedActivityDefinition?.dynamicValue, 'serviceRequest.code')
        );

        const useContext = planDefinition?.useContext?.map((e) => e?.valueCodeableConcept);
        const [ planDefinitionType, planDefinitionPerformerCategory ] = [
          'ph-service-request-performer-sourcing',
          'ph-service-request-performer-category'
        ].map((systemSubstring) => {
          const concept = useContext?.find((c) =>
            c?.coding?.some((coding) => coding?.system?.includes(systemSubstring))
          );
        
          return concept?.coding?.[0] ?? null;
        });

        const isNAT = natPlanDefinitionNames?.find((e) => e === planDefinition?.name) ? true : false;
        
        return {
          reference: planDefinition?.id ? `${planDefinition?.resourceType}/${planDefinition?.id}` : null,
          title: planDefinition?.title,
          topic: planDefinition?.topic,
          isNAT,
          planDefinitionIdentifierValue: planDefinition?.identifier?.[0]?.value,
          planDefinitionType: planDefinitionType?.code,
          planDefinitionPerformerCategory: {
            value: planDefinitionPerformerCategory?.code,
            label: planDefinitionPerformerCategory?.display
          },
          serviceRequestIntent,
          serviceRequestStatus,
          serviceRequestCode : serviceRequestCode ? { 
            label: serviceRequestCode?.display,
            value: {
              healthcareService: {
                "coding": [{...serviceRequestCode}],
                "text": serviceRequestCode?.display
              }
            }
          } : null
        }
      });
      return mapPlanActivityDefinition;
    } catch (error) {
      return [];
    }
  },

  getLocationFromOrganizationUsers: async (userOrganizations: string[]): Promise<Location[] | undefined> => {
    const bundleLocation = await fhirClient.search(`Location?organization=${userOrganizations?.join(",")}`);
    if (!bundleLocation?.entry?.length) return [];
    const location = bundleLocation.entry.reduce<(Location)[]>((resources, { resource }) => {
      if (!resource) return resources;
      return [...resources, resource as Location];
    }, []);
    return location;
  },

  sync: async (id: string) => {
    const response = await ReferralApi.sync(id);
    if (!response) return undefined;
    return response;
  }
}
